import React, { useState } from 'react'
import { useForm } from 'react-hook-form';
import { ArrowLeft, Mail, ChevronRight, Lock } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { loginUser } from '../store/actions/user_actions';

export default function LoginPage() {

  const dispatch = useDispatch();
  const [succeed, setSucceed] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loginMessage, setLoginMessage] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, },
  } = useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {

    try {
      // Clear existing token
      sessionStorage.removeItem('food_recipe');
      const response = await dispatch(loginUser(data));
      setLoading(true);

      const newToken = response.payload;
      console.log(newToken);
      if (newToken?.data?.token) {
        setSucceed(true)
        setFailed(false)
        setTimeout(() => {
          setLoginMessage(newToken.message)
          setSucceed(false);
          setLoading(false)
          navigate('/dashboard');

        }, 4000)

      } else {
        setFailed(true)
        setLoginMessage(newToken.message)
        setSucceed(false);
        setLoading(false)
        setTimeout(() => {
          setSucceed(false);
        }, 4000)
      }
    } catch (error) {
      console.log(error)
      setFailed(true);
      setLoginMessage("An unexpected error occurred.");
      setLoading(false);
      setTimeout(() => setFailed(false), 4000);
    }
  };

  return (
    <div className="min-h-screen bg-premium-dark flex items-center justify-center font-poppins relative overflow-hidden">
      {/* Dynamic Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-premium-teal/30 rounded-full blur-[120px] animate-pulse-slow"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-premium-gold/10 rounded-full blur-[120px]"></div>
      </div>

      <div className="relative z-10 w-full max-w-[1000px] m-4 animate-fade-in-up">
        {/* Glassmorphic Container */}
        <div className="glass-card rounded-[3rem] overflow-hidden flex flex-col md:flex-row shadow-2xl border-white/5">

          {/* Left Side - Visual/Experience */}
          <div className="md:w-[45%] relative bg-premium-teal p-12 flex flex-col justify-between overflow-hidden">
            <div className="absolute inset-0 z-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
            <div className="absolute -right-20 -top-20 w-64 h-64 bg-premium-gold rounded-full blur-[80px] opacity-20"></div>

            <div className="relative z-10">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-premium-gold/80 hover:text-premium-gold transition-all duration-300 mb-12 group uppercase tracking-widest text-xs font-black"
              >
                <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> Back to Home
              </button>
              <div className="space-y-2">
                <span className="text-premium-gold font-black tracking-[0.3em] text-sm uppercase px-3 py-1 bg-white/5 rounded-full inline-block">Collector's Entrance</span>
                <h1 className="text-7xl font-black tracking-tighter text-white">
                  O<span className="text-premium-gold">DAZ</span>
                </h1>
              </div>
              <p className="mt-8 text-teal-100/60 leading-relaxed font-light text-lg">
                Reignite your culinary journey. Access the most exclusive collection of flavor management tools.
              </p>
            </div>

            <div className="relative z-10 mt-12">
              <div className="bg-white/5 backdrop-blur-xl p-8 rounded-[2rem] border border-white/10 shadow-inner">
                <p className="italic text-teal-50 text-base leading-relaxed font-light">"Excellence is not a skill, it is an attitude."</p>
                <div className="mt-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-premium-gold to-premium-gold-light flex items-center justify-center text-premium-teal font-black text-xl shadow-lg">SJ</div>
                  <div>
                    <p className="text-sm font-black text-white uppercase tracking-wider">Steve Jobs</p>
                    <p className="text-xs text-premium-gold/70 font-semibold tracking-widest uppercase">Icon of Innovation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Refined Login Form */}
          <div className="md:w-[55%] p-10 md:p-16 bg-white/[0.02] backdrop-blur-3xl flex flex-col justify-center">
            <div className="mb-10 animate-fade-in-up [animation-delay:100ms]">
              <h2 className="text-4xl font-black text-white tracking-tight">Welcome Back</h2>
              <p className="text-gray-400 mt-2 font-light">Authenticate your credentials to proceed.</p>
            </div>

            {/* Status Messages */}
            <div className="min-h-[3.5rem] mb-4">
              {succeed && (
                <div className="p-4 rounded-2xl bg-green-500/10 text-green-400 border border-green-500/20 flex items-center gap-3 animate-fade-in-up">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-ping"></span>
                  <span className="text-sm font-medium">{loginMessage || "Authentication successful."}</span>
                </div>
              )}
              {failed && (
                <div className="p-4 rounded-2xl bg-red-500/10 text-red-400 border border-red-500/20 flex items-center gap-3 shake">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  <span className="text-sm font-medium">{loginMessage || "Authentication failed. Verify details."}</span>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 animate-fade-in-up [animation-delay:200ms]">
              <div className="group">
                <label className="block text-xs font-black text-premium-gold uppercase tracking-[0.2em] mb-2 px-1">Username</label>
                <div className="relative group/input">
                  <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-500 group-focus-within/input:text-premium-gold transition-colors" />
                  </div>
                  <input
                    placeholder="email@example.com"
                    type="text"
                    {...register("username", {
                      required: "Identity is required",
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9._-]+\.[a-zA-Z]{2,}$/,
                        message: "Invalid email structure",
                      },
                    })}
                    className={`block w-full pl-14 pr-5 py-4 bg-white/[0.03] border ${errors.username ? 'border-red-500/50' : 'border-white/10'} rounded-[1.5rem] text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-transparent transition-all duration-300 group-hover:bg-white/[0.05]`}
                  />
                </div>
                {errors.username && <p className="mt-2 text-xs font-semibold text-red-500 px-1">{errors.username.message}</p>}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2 px-1">
                  <label className="block text-xs font-black text-premium-gold uppercase tracking-[0.2em]">Password</label>
                  <a href="/forgot" className="text-xs font-bold text-gray-500 hover:text-premium-gold transition-colors uppercase tracking-widest">Forgot Password?</a>
                </div>
                <div className="relative group/input">
                  <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-500 group-focus-within/input:text-premium-gold transition-colors" />
                  </div>
                  <input
                    placeholder="••••••••"
                    type="password"
                    {...register("password", {
                      required: "Secret is required",
                      minLength: { value: 6, message: "Secret must be 6+ characters" },
                    })}
                    className={`block w-full pl-14 pr-5 py-4 bg-white/[0.03] border ${errors.password ? 'border-red-500/50' : 'border-white/10'} rounded-[1.5rem] text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-premium-gold/50 focus:border-transparent transition-all duration-300 group-hover:bg-white/[0.05]`}
                  />
                </div>
                {errors.password && <p className="mt-2 text-xs font-semibold text-red-500 px-1">{errors.password.message}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="premium-button w-full flex justify-center items-center gap-3 py-5 px-4 rounded-[1.5rem] text-sm font-black text-premium-teal bg-premium-gold hover:bg-premium-gold-light shadow-2xl shadow-premium-gold/20 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {loading ? (
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-[3px] border-premium-teal border-t-transparent"></div>
                    <span className="uppercase tracking-widest">Authenticating...</span>
                  </div>
                ) : (
                  <>
                    <span className="uppercase tracking-widest text-base">Sign in Now</span>
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>

            {/* <div className="mt-12 text-center animate-fade-in-up [animation-delay:300ms]">
              <p className="text-sm text-gray-500 font-light">
                Not a member of the elite?{' '}
                <a href="/register" className="font-black text-premium-gold hover:text-premium-gold-light transition-colors uppercase tracking-widest text-xs">
                  Request Access
                </a>
              </p>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  )
}

